import moment from 'moment';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import Button from '../../atoms/Buttons';
import DateTimeRange from '../../atoms/DateTimeRange';
import Label from '../../atoms/Label';
import Select from '../../atoms/Select';
import TextBox from '../../atoms/TextBox';

type Query = {
  status: string | null;
  receipt_no: string | null;
  customer_id: string | null;
  pay_limit_from: moment.Moment | null | undefined;
  pay_limit_to: moment.Moment | null | undefined;
};

type Props = {
  query: Query;
  setQuery: (query: Query) => void;
};

type FormFilterParams = {
  status: string | null;
  pay_limit: {
    from: moment.Moment | null;
    to: moment.Moment | null;
  };
  receipt_no: string | null;
  customer_id: string | null;
};

const FilterFormWrapper = styled.div`
  margin-top: 16px;

  ${Label}, ${DateTimeRange} {
    margin: 0;
  }
`;

const FilterRow = styled.div`
  margin: 8px 0;
  display: flex;
  gap: 6px;
  align-items: center;
`;

const createFormValues = (query: Query) => ({
  status: query.status ?? null,
  pay_limit: {
    from: query.pay_limit_from ?? null,
    to: query.pay_limit_to ?? null,
  },
  receipt_no: query.receipt_no ?? null,
  customer_id: query.customer_id ?? null,
});

const STATUS_OPTIONS = [
  { label: '指定なし', value: null },
  { label: '申込完了', value: 'applied' },
  { label: '支払完了', value: 'payed' },
  { label: '取消', value: 'canceled' },
  { label: '期限切れ', value: 'expired' },
];

const FilterForm = ({ query, setQuery }: Props) => {
  const { handleSubmit, control, register, reset } = useForm<FormFilterParams>({
    defaultValues: createFormValues(query),
  });

  const onSubmit = (data: FormFilterParams) => {
    const { pay_limit, status, receipt_no, customer_id } = data;

    setQuery({
      pay_limit_from: pay_limit.from,
      pay_limit_to: pay_limit.to,
      status,
      receipt_no: !receipt_no ? null : receipt_no,
      customer_id: !customer_id ? null : customer_id,
    });
  };

  useEffect(() => {
    reset(createFormValues(query));
  }, [query, reset]);

  return (
    <FilterFormWrapper>
      <FilterRow>
        <Label>状態</Label>
        <Controller
          name="status"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Select
              isMulti={false}
              options={STATUS_OPTIONS}
              value={STATUS_OPTIONS.find((x) => x.value === value)}
              onChange={(option) => onChange(option.value)}
            />
          )}
        />

        <Label>支払期限</Label>
        <Controller
          name="pay_limit"
          control={control}
          render={({ field: { value, onChange } }) => (
            <DateTimeRange
              dateRange={{
                from: value.from,
                to: value.to,
              }}
              setDateRange={(range) => {
                if (!range?.from?.isSame(value?.from)) {
                  onChange({
                    from: range.from ?? null,
                    to:
                      range.from?.clone().add(3, 'months').subtract(1, 'day').endOf('day') ?? null,
                  });
                  return;
                }

                onChange({ from: range.from ?? null, to: range.to ?? null });
              }}
              dateTimeProps={{
                fromProps: { showIcon: true },
                toProps: {
                  showIcon: true,
                  isValidDate: (current) => {
                    if (value.from?.isAfter(current)) {
                      return false;
                    }

                    if (value.from?.clone().add(3, 'months').subtract(1, 'day').isBefore(current)) {
                      return false;
                    }

                    return true;
                  },
                },
              }}
            />
          )}
        />
      </FilterRow>

      <FilterRow>
        <Label>受付番号</Label>
        <TextBox {...register('receipt_no')} />

        <Label>お客様ID</Label>
        <TextBox {...register('customer_id')} />
      </FilterRow>

      <Button onClick={handleSubmit(onSubmit)}>検索</Button>
    </FilterFormWrapper>
  );
};

export default FilterForm;
